import { useEffect, useMemo, useState } from "react";
import { crDate, crDateTime, crLocalToISO, isoToCRInput } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Check, Loader2, X, Package, Store, Code2, Plus, Pencil, Trash2, Save, Eye, EyeOff,
  ExternalLink, Copy, FileText, RefreshCw, MapPin, Users, Star, Tag, Flame, Percent, Handshake,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_LABEL = { pending: "Pendiente", confirmed: "Confirmado", rejected: "Rechazado" };
const STATUS_CLASS = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  confirmed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-100 text-rose-700 border-rose-200",
};

function money(n) { return `USD ${Number(n || 0).toFixed(2)}`; }
function humanDate(iso) { if (!iso) return "—"; return crDateTime(iso) || iso; }

/* --------------------------- ORDERS TAB --------------------------- */
function OrdersPanel() {
  const [status, setStatus] = useState("pending");
  const [orders, setOrders] = useState(null);
  const [receiptOrder, setReceiptOrder] = useState(null);
  const [decision, setDecision] = useState(null); // { order, kind: "confirm"|"reject" }
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/store/orders?status=${status}`)
      .then((r) => setOrders(r.data || []))
      .catch(() => setOrders([]));

  useEffect(() => { setOrders(null); load(); /* eslint-disable-next-line */ }, [status]);

  const submitDecision = async () => {
    if (!decision) return;
    setBusy(true);
    try {
      await api.post(`/store/orders/${decision.order.id}/${decision.kind}`, { note: note || null });
      toast.success(decision.kind === "confirm" ? "Pedido confirmado. Le enviamos el acceso." : "Pedido rechazado.");
      setDecision(null); setNote(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos procesar el pedido");
    } finally { setBusy(false); }
  };

  const counts = orders?.length ?? 0;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["pending", "confirmed", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition ${
              status === s
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
            }`}
            data-testid={`orders-tab-${s}`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
        <button onClick={load} className="ml-auto inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100" data-testid="orders-refresh">
          <RefreshCw className="h-3 w-3" /> Actualizar
        </button>
      </div>

      {orders === null ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : counts === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-slate-500" data-testid="orders-empty">
          Sin pedidos en esta bandeja.
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLASS[o.status]}`}>{STATUS_LABEL[o.status]}</span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">{o.id}</span>
                    <span className="text-[10px] text-slate-500">{humanDate(o.created_at)}</span>
                  </div>
                  <h3 className="mt-1.5 font-semibold text-slate-900">{o.product_name}</h3>
                  <div className="mt-0.5 text-sm text-slate-600">
                    <strong>{o.name}</strong> · {o.email}{o.phone ? ` · ${o.phone}` : ""}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>{money(o.amount)}{o.coupon_code && o.original_amount ? <span className="ml-1 line-through text-slate-400">{money(o.original_amount)}</span> : null}</span>
                    {o.coupon_code && (
                      <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700 border border-emerald-200" data-testid={`coupon-${o.id}`}>
                        <Tag className="inline h-3 w-3 mr-0.5" />{o.coupon_code} · -{money(o.coupon_discount || 0)}
                      </span>
                    )}
                    {o.issuer_name && <span>Emisor: {o.issuer_name}</span>}
                    {o.sede_name && <span><MapPin className="inline h-3 w-3 mr-0.5" />{o.sede_name}</span>}
                    {o.group_name && <span>Grupo: {o.group_name}</span>}
                    {o.product_type === "private" && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-700">Privado</span>}
                    {o.status === "pending" && typeof o.current_seats_left === "number" && (
                      <span className={o.current_seats_left <= 0 ? "text-rose-600 font-semibold" : ""}>
                        <Users className="inline h-3 w-3 mr-0.5" />
                        {o.current_seats_left <= 0 ? "Grupo lleno" : `${o.current_seats_left} cupos libres`}
                      </span>
                    )}
                  </div>
                  {o.notes && <p className="mt-2 rounded-lg bg-slate-50 border border-slate-100 p-2 text-xs text-slate-600 italic">"{o.notes}"</p>}
                  {o.decision_note && <p className="mt-2 rounded-lg bg-slate-50 border border-slate-100 p-2 text-xs text-slate-600">Nota admin: {o.decision_note}</p>}
                </div>
                <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setReceiptOrder(o)} data-testid={`view-receipt-${o.id}`}>
                    <FileText className="h-3.5 w-3.5 mr-1" /> Ver comprobante
                  </Button>
                  {o.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => { setDecision({ order: o, kind: "confirm" }); setNote(""); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`confirm-${o.id}`}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Confirmar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setDecision({ order: o, kind: "reject" }); setNote(""); }} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`reject-${o.id}`}>
                        <X className="h-3.5 w-3.5 mr-1" /> Rechazar
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Comprobante */}
      <Dialog open={!!receiptOrder} onOpenChange={(o) => !o && setReceiptOrder(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Comprobante · {receiptOrder?.name}</DialogTitle>
            <DialogDescription>{receiptOrder?.product_name} · {money(receiptOrder?.amount)}</DialogDescription>
          </DialogHeader>
          {receiptOrder && (
            <ReceiptViewer orderId={receiptOrder.id} contentType={receiptOrder.receipt_content_type} />
          )}
        </DialogContent>
      </Dialog>

      {/* Decisión */}
      <AlertDialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {decision?.kind === "confirm" ? "Confirmar pedido" : "Rechazar pedido"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {decision?.kind === "confirm"
                ? "Se creará la cuenta del estudiante, la matrícula y se le enviará el acceso por correo."
                : "Se le avisará por correo al estudiante que el pago no pudo confirmarse."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Nota (opcional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="decision-note"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => { e.preventDefault(); submitDecision(); }}
              className={decision?.kind === "reject" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}
              data-testid="decision-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (decision?.kind === "confirm" ? "Confirmar" : "Rechazar")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ReceiptViewer({ orderId, contentType }) {
  const [state, setState] = useState({ url: null, type: null, err: null });
  useEffect(() => {
    let alive = true;
    let objUrl = null;
    (async () => {
      try {
        const r = await api.get(`/store/orders/${orderId}/receipt`, { responseType: "blob" });
        if (!alive) return;
        const type = r.headers?.["content-type"] || contentType || r.data.type;
        objUrl = URL.createObjectURL(r.data);
        setState({ url: objUrl, type, err: null });
      } catch {
        if (alive) setState({ url: null, type: null, err: "No pudimos cargar el comprobante" });
      }
    })();
    return () => { alive = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [orderId, contentType]);

  if (state.err) return <div className="p-6 text-center text-sm text-rose-600">{state.err}</div>;
  if (!state.url) return <div className="p-6 flex items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if ((state.type || "").includes("pdf")) return <iframe src={state.url} className="h-[70vh] w-full rounded-lg border" title="comprobante" />;
  return <img src={state.url} alt="comprobante" className="mx-auto max-h-[70vh] rounded-lg border" />;
}

/* --------------------------- PRODUCTS TAB --------------------------- */
const EMPTY_PRODUCT = {
  product_type: "group", program_id: "", group_id: "", sede_id: "", name: "",
  description: "", modality: "", price: "", payment_link: "", issuer_id: "",
  classes_included: "", visible: true, highlight: false, order: "",
  launch_price: "", launch_ends_at: "",
};

function ProductsPanel() {
  const [products, setProducts] = useState(null);
  const [refs, setRefs] = useState({ programs: [], groups: [], sedes: [], issuers: [] });
  const [editing, setEditing] = useState(null); // product being edited or "new"
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => Promise.all([
    api.get("/store/products").then((r) => setProducts(r.data || [])).catch(() => setProducts([])),
    api.get("/store/refs").then((r) => setRefs(r.data || { programs: [], groups: [], sedes: [], issuers: [] })).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_PRODUCT); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      product_type: p.product_type || "group",
      program_id: p.program_id || "",
      group_id: p.group_id || "",
      sede_id: p.sede_id || "",
      name: p.name || "",
      description: p.description || "",
      modality: p.modality || "",
      price: p.price ?? "",
      payment_link: p.payment_link || "",
      issuer_id: p.issuer_id || "",
      classes_included: p.classes_included ?? "",
      visible: p.visible !== false,
      highlight: !!p.highlight,
      order: p.order ?? "",
      launch_price: p.launch_price ?? "",
      launch_ends_at: isoToCRInput(p.launch_ends_at),
    });
    setEditing(p);
  };

  const filteredGroups = useMemo(() => {
    if (!form.program_id) return [];
    return refs.groups.filter((g) => g.program_id === form.program_id);
  }, [refs.groups, form.program_id]);

  const save = async () => {
    if (!form.program_id) { toast.error("Elegí un programa"); return; }
    if (form.product_type === "group" && !form.group_id) { toast.error("Elegí el grupo"); return; }
    setBusy(true);
    const body = {
      product_type: form.product_type,
      program_id: form.program_id,
      group_id: form.product_type === "group" ? form.group_id : null,
      sede_id: form.product_type === "private" ? (form.sede_id || null) : null,
      name: form.name || null,
      description: form.description || "",
      modality: form.modality || "",
      price: form.price === "" ? null : Number(form.price),
      launch_price: form.launch_price === "" ? null : Number(form.launch_price),
      launch_ends_at: crLocalToISO(form.launch_ends_at) || null,
      payment_link: form.payment_link || "",
      issuer_id: form.issuer_id || null,
      classes_included: form.classes_included === "" ? null : Number(form.classes_included),
      visible: !!form.visible,
      highlight: !!form.highlight,
      order: form.order === "" ? null : Number(form.order),
    };
    try {
      if (editing === "new") await api.post("/store/products", body);
      else await api.patch(`/store/products/${editing.id}`, body);
      toast.success("Guardado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos guardar");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/store/products/${toDelete.id}`);
      toast.success("Producto eliminado");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos eliminar");
    }
  };

  const toggleVisible = async (p) => {
    try {
      await api.patch(`/store/products/${p.id}`, {
        product_type: p.product_type, program_id: p.program_id, group_id: p.group_id,
        sede_id: p.sede_id, name: p.name, description: p.description, modality: p.modality,
        price: p.price, launch_price: p.launch_price ?? null, launch_ends_at: p.launch_ends_at || null,
        payment_link: p.payment_link, issuer_id: p.issuer_id,
        classes_included: p.classes_included, visible: !p.visible, highlight: p.highlight,
        order: p.order,
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No pudimos actualizar"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="text-xs text-slate-500">
          Cada producto se muestra en <code className="rounded bg-slate-100 px-1">/paquetes</code>. Los grupales toman los cupos del grupo asociado.
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="new-product-btn">
          <Plus className="h-4 w-4 mr-1" /> Nuevo paquete
        </Button>
      </div>

      {products === null ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : products.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-slate-500" data-testid="products-empty">
          Todavía no hay paquetes. Creá uno para publicarlo en la tienda.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {products.map((p) => (
            <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`product-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1 text-[10px] uppercase tracking-wider">
                    <span className={`rounded-full px-2 py-0.5 font-bold ${p.product_type === "private" ? "bg-slate-900 text-white" : "bg-amber-100 text-amber-800"}`}>
                      {p.product_type === "private" ? "Privado" : "Grupal"}
                    </span>
                    {p.visible ? (
                      <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 font-bold">Visible</span>
                    ) : (
                      <span className="rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 font-bold">Oculto</span>
                    )}
                    {p.highlight && <span className="rounded-full bg-amber-400 text-amber-950 px-2 py-0.5 font-bold">Destacado</span>}
                  </div>
                  <h3 className="mt-1.5 font-semibold text-slate-900 truncate">{p.name}</h3>
                  <div className="mt-0.5 text-xs text-slate-500 truncate">{p.program_name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-lg font-bold text-slate-900">${Number(p.price || 0).toFixed(0)}</div>
                  <div className="text-[10px] text-slate-400">USD</div>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                {p.sede_name && <span><MapPin className="inline h-3 w-3 mr-0.5" />{p.sede_name}</span>}
                {p.group_name && <span>Grupo: {p.group_name}</span>}
                {typeof p.seats_left === "number" && (
                  <span className={p.is_full ? "text-rose-600 font-semibold" : ""}>
                    <Users className="inline h-3 w-3 mr-0.5" />
                    {p.is_full ? "Lleno" : `${p.seats_left}/${p.group_capacity} cupos`}
                  </span>
                )}
                {p.issuer_name && <span>{p.issuer_name}</span>}
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}>
                  <Pencil className="h-3 w-3 mr-1" /> Editar
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleVisible(p)} data-testid={`toggle-product-${p.id}`}>
                  {p.visible ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </Button>
                <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setToDelete(p)} data-testid={`del-product-${p.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Nuevo paquete" : "Editar paquete"}</DialogTitle>
            <DialogDescription>
              Los grupales se conectan a un grupo abierto y respetan sus cupos. Los privados se
              publican como plan a medida — al confirmar, armás el horario.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-600">Tipo de producto</label>
              <div className="mt-1 flex gap-2">
                {["group", "private"].map((t) => (
                  <button key={t} type="button" onClick={() => setForm((f) => ({ ...f, product_type: t, group_id: "" }))}
                    className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                      form.product_type === t ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`type-${t}`}
                  >
                    {t === "group" ? "Grupal (conectado a grupo)" : "Privado (plan a medida)"}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Programa *</label>
              <Select value={form.program_id} onValueChange={(v) => setForm((f) => ({ ...f, program_id: v, group_id: "" }))}>
                <SelectTrigger className="mt-1" data-testid="program-select"><SelectValue placeholder="Elegí programa" /></SelectTrigger>
                <SelectContent>
                  {refs.programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {form.product_type === "group" ? (
              <div>
                <label className="text-xs font-semibold text-slate-600">Grupo asociado *</label>
                <Select value={form.group_id} onValueChange={(v) => setForm((f) => ({ ...f, group_id: v }))} disabled={!form.program_id}>
                  <SelectTrigger className="mt-1" data-testid="group-select"><SelectValue placeholder={form.program_id ? "Elegí grupo" : "Elegí programa primero"} /></SelectTrigger>
                  <SelectContent>
                    {filteredGroups.map((g) => {
                      const seatsText = g.seats ? ` · ${Math.max(g.seats.max - g.seats.taken, 0)}/${g.seats.max} cupos` : "";
                      return <SelectItem key={g.id} value={g.id}>{g.name}{seatsText}</SelectItem>;
                    })}
                    {form.program_id && filteredGroups.length === 0 && (
                      <div className="p-2 text-xs text-slate-500">No hay grupos abiertos para este programa.</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold text-slate-600">Sede (opcional)</label>
                <Select value={form.sede_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, sede_id: v === "__none__" ? "" : v }))}>
                  <SelectTrigger className="mt-1" data-testid="sede-select"><SelectValue placeholder="Sin sede específica" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Sin sede —</SelectItem>
                    {refs.sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-600">Nombre del paquete (opcional)</label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Deja vacío para usar el nombre del programa" className="mt-1" data-testid="name-input" />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-600">Descripción para la tienda</label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1" placeholder="Qué incluye, para quién es, resultados…" data-testid="description-input" />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Modalidad</label>
              <Input value={form.modality} onChange={(e) => setForm((f) => ({ ...f, modality: e.target.value }))} placeholder="Ej: Online, Presencial…" className="mt-1" data-testid="modality-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Precio (USD)</label>
              <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} placeholder="Vacío = precio del programa" className="mt-1" data-testid="price-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Clases incluidas</label>
              <Input type="number" min="0" value={form.classes_included} onChange={(e) => setForm((f) => ({ ...f, classes_included: e.target.value }))} placeholder="Vacío = del programa" className="mt-1" data-testid="classes-input" />
            </div>

            <div>
              <label className="text-xs font-semibold text-rose-700">Precio de lanzamiento (opcional)</label>
              <Input type="number" min="0" step="0.01" value={form.launch_price}
                onChange={(e) => setForm((f) => ({ ...f, launch_price: e.target.value }))}
                placeholder="Precio promocional sin código" className="mt-1" data-testid="launch-price-input" />
              <p className="text-[10px] text-slate-500 mt-0.5">Baja el precio público sin necesidad de cupón.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-rose-700">Termina el (opcional)</label>
              <Input type="datetime-local" value={form.launch_ends_at}
                onChange={(e) => setForm((f) => ({ ...f, launch_ends_at: e.target.value }))}
                className="mt-1" data-testid="launch-ends-input" />
              <p className="text-[10px] text-slate-500 mt-0.5">Vacío = lanzamiento sin fecha de fin.</p>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Orden</label>
              <Input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} placeholder="Menor = aparece antes" className="mt-1" data-testid="order-input" />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-600">Link de pago (Stripe, PayPal, etc.)</label>
              <Input value={form.payment_link} onChange={(e) => setForm((f) => ({ ...f, payment_link: e.target.value }))} placeholder="https://..." className="mt-1" data-testid="paylink-input" />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-600">Empresa emisora</label>
              <Select value={form.issuer_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, issuer_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="mt-1" data-testid="issuer-select"><SelectValue placeholder="Elegí emisor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin emisor —</SelectItem>
                  {refs.issuers.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2 flex flex-wrap gap-4 pt-1">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.visible} onChange={(e) => setForm((f) => ({ ...f, visible: e.target.checked }))} data-testid="visible-check" />
                <Eye className="h-3.5 w-3.5" /> Visible en tienda
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.highlight} onChange={(e) => setForm((f) => ({ ...f, highlight: e.target.checked }))} data-testid="highlight-check" />
                <Star className="h-3.5 w-3.5" /> Destacar
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Cancelar</Button>
            <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="save-product">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar paquete</AlertDialogTitle>
            <AlertDialogDescription>
              Se quitará "{toDelete?.name}" de la tienda. Los pedidos previos se mantienen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* --------------------------- EMBED TAB --------------------------- */
function EmbedPanel() {
  const [sedes, setSedes] = useState([]);
  const [sedeId, setSedeId] = useState("");
  useEffect(() => { api.get("/store/sedes").then((r) => setSedes(r.data || [])).catch(() => {}); }, []);

  const base = window.location.origin;
  const paquetesUrl = `${base}/paquetes${sedeId ? `?sede=${sedeId}` : ""}`;
  const embedUrl = `${base}/paquetes?embed=1${sedeId ? `&sede=${sedeId}` : ""}`;
  const iframe = `<iframe src="${embedUrl}" style="width:100%;min-height:900px;border:0;" title="Paquetes We Speak Spanish"></iframe>`;

  const copy = (txt, msg = "Copiado") => { navigator.clipboard.writeText(txt); toast.success(msg); };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-slate-900">Enlace público</h3>
            <p className="text-xs text-slate-500 mt-1">Compartilo en redes o mandalo directo a un cliente.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={sedeId || "__all__"} onValueChange={(v) => setSedeId(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-56" data-testid="embed-sede"><SelectValue placeholder="Todas las sedes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas las sedes</SelectItem>
                {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-slate-50 border border-slate-200 p-3">
          <code className="flex-1 truncate text-xs text-slate-700" data-testid="public-url">{paquetesUrl}</code>
          <Button size="sm" variant="outline" onClick={() => copy(paquetesUrl)} data-testid="copy-public"><Copy className="h-3 w-3 mr-1" /> Copiar</Button>
          <a href={paquetesUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"><ExternalLink className="h-3 w-3" /> Abrir</a>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-semibold text-slate-900">Embeber en tu website</h3>
        <p className="text-xs text-slate-500 mt-1">
          Pegá este código en wespeakspanish-cr.com donde quieras que salga la tienda. El filtro
          por sede se hereda del selector de arriba.
        </p>
        <div className="mt-4 rounded-xl bg-slate-950 text-slate-100 p-4">
          <pre className="text-xs whitespace-pre-wrap break-all" data-testid="embed-snippet">{iframe}</pre>
        </div>
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={() => copy(iframe, "Snippet copiado")} data-testid="copy-embed">
            <Copy className="h-3 w-3 mr-1" /> Copiar snippet
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5">
        <h3 className="font-semibold text-slate-900">Vista previa</h3>
        <p className="text-xs text-slate-500 mt-1">Así se ve el embebido (fondo transparente, adaptado a fondo claro).</p>
        <iframe
          src={embedUrl}
          style={{ width: "100%", minHeight: 720, border: 0, marginTop: 16, borderRadius: 12, background: "#fff" }}
          title="preview-embed"
          data-testid="embed-preview"
        />
      </div>
    </div>
  );
}

/* --------------------------- COUPONS TAB --------------------------- */
const EMPTY_COUPON = {
  code: "", description: "", discount_type: "percentage", discount_value: "",
  scope: "all", product_ids: [], sede_ids: [], affiliate_code: "",
  starts_at: "", ends_at: "", max_uses: "", one_per_email: false, active: true,
};

function CouponsPanel() {
  const [coupons, setCoupons] = useState(null);
  const [refs, setRefs] = useState({ products: [], sedes: [], affiliates: [] });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_COUPON);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => Promise.all([
    api.get("/store/coupons").then((r) => setCoupons(r.data || [])).catch(() => setCoupons([])),
    Promise.all([
      api.get("/store/products").then((r) => r.data || []).catch(() => []),
      api.get("/store/refs").then((r) => r.data || {}).catch(() => ({})),
      api.get("/affiliates?active=1").then((r) => (r.data?.items || r.data || [])).catch(() => []),
    ]).then(([products, storeRefs, affiliates]) => setRefs({
      products, sedes: storeRefs.sedes || [], affiliates,
    })),
  ]);
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_COUPON); setEditing("new"); };
  const openEdit = (c) => {
    setForm({
      code: c.code, description: c.description || "",
      discount_type: c.discount_type || "percentage",
      discount_value: c.discount_value ?? "",
      scope: c.scope || "all",
      product_ids: c.product_ids || [],
      sede_ids: c.sede_ids || [],
      affiliate_code: c.affiliate_code || "",
      starts_at: isoToCRInput(c.starts_at),
      ends_at: isoToCRInput(c.ends_at),
      max_uses: c.max_uses ?? "",
      one_per_email: !!c.one_per_email,
      active: c.active !== false,
    });
    setEditing(c);
  };

  const save = async () => {
    if (!form.code.trim()) { toast.error("Poné un código"); return; }
    if (!form.discount_value || Number(form.discount_value) <= 0) { toast.error("Descuento inválido"); return; }
    if (form.scope === "products" && !form.product_ids.length) { toast.error("Elegí al menos un paquete"); return; }
    if (form.scope === "sedes" && !form.sede_ids.length) { toast.error("Elegí al menos una sede"); return; }
    setBusy(true);
    const body = {
      code: form.code.trim().toUpperCase(),
      description: form.description || "",
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      scope: form.scope,
      product_ids: form.scope === "products" ? form.product_ids : [],
      sede_ids: form.scope === "sedes" ? form.sede_ids : [],
      affiliate_code: form.affiliate_code || null,
      starts_at: crLocalToISO(form.starts_at) || null,
      ends_at: crLocalToISO(form.ends_at) || null,
      max_uses: form.max_uses === "" ? null : Number(form.max_uses),
      one_per_email: !!form.one_per_email,
      active: !!form.active,
    };
    try {
      if (editing === "new") await api.post("/store/coupons", body);
      else await api.patch(`/store/coupons/${editing.id}`, body);
      toast.success("Guardado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos guardar");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/store/coupons/${toDelete.id}`);
      toast.success("Cupón eliminado");
      setToDelete(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No pudimos eliminar"); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/store/coupons/${c.id}`, {
        code: c.code, description: c.description || "",
        discount_type: c.discount_type, discount_value: c.discount_value,
        scope: c.scope, product_ids: c.product_ids || [], sede_ids: c.sede_ids || [],
        affiliate_code: c.affiliate_code || null,
        starts_at: c.starts_at || null, ends_at: c.ends_at || null,
        max_uses: c.max_uses ?? null, one_per_email: !!c.one_per_email,
        active: !c.active,
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No pudimos actualizar"); }
  };

  const toggleInList = (list, val) => list.includes(val) ? list.filter((x) => x !== val) : [...list, val];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="text-xs text-slate-500">
          Cupones para campañas (Black Friday, referidos, afiliados). El código lo escribe el cliente en el modal del pedido.
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="new-coupon-btn">
          <Plus className="h-4 w-4 mr-1" /> Nuevo cupón
        </Button>
      </div>

      {coupons === null ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : coupons.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-slate-500" data-testid="coupons-empty">
          Todavía no hay cupones. Creá el primero para lanzar tu próxima campaña.
        </div>
      ) : (
        <div className="space-y-2">
          {coupons.map((c) => (
            <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`coupon-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="rounded-md bg-slate-900 text-white px-2 py-0.5 text-sm font-bold tracking-wider">{c.code}</code>
                    <span className="text-sm font-semibold text-emerald-700">
                      {c.discount_type === "percentage" ? `${Number(c.discount_value).toFixed(0)}%` : `-USD ${Number(c.discount_value).toFixed(2)}`} off
                    </span>
                    {!c.active && <span className="rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 text-[10px] font-bold uppercase">Inactivo</span>}
                    {c.affiliate_code && (
                      <span className="rounded-full bg-sky-100 text-sky-700 px-2 py-0.5 text-[10px] font-bold uppercase" title={c.affiliate_name || c.affiliate_code}>
                        <Handshake className="inline h-3 w-3 mr-0.5" />Afiliado {c.affiliate_code}
                      </span>
                    )}
                    {c.scope !== "all" && (
                      <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase">
                        {c.scope === "products" ? `${(c.product_ids || []).length} paquetes` : `${(c.sede_ids || []).length} sedes`}
                      </span>
                    )}
                  </div>
                  {c.description && <p className="mt-1 text-sm text-slate-600">{c.description}</p>}
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    {c.starts_at && <span>Desde {crDate(c.starts_at)}</span>}
                    {c.ends_at && <span>Hasta {crDate(c.ends_at)}</span>}
                    <span>Usos: {c.uses_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</span>
                    {c.one_per_email && <span>· 1 por correo</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => toggleActive(c)} data-testid={`toggle-coupon-${c.id}`}>
                    {c.active ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-coupon-${c.id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setToDelete(c)} data-testid={`del-coupon-${c.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Nuevo cupón" : `Editar ${editing?.code}`}</DialogTitle>
            <DialogDescription>Los cupones aplican descuento al total del pedido. Si asociás un afiliado, la matrícula que se cree al confirmar quedará atribuida a él y genera comisión.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-semibold text-slate-600">Código *</label>
              <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="BLACKFRIDAY25" className="mt-1 font-mono uppercase" data-testid="coupon-code" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Descripción interna</label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Campaña Black Friday 2026" className="mt-1" data-testid="coupon-desc" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Tipo</label>
              <Select value={form.discount_type} onValueChange={(v) => setForm((f) => ({ ...f, discount_type: v }))}>
                <SelectTrigger className="mt-1" data-testid="coupon-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage"><Percent className="inline h-3 w-3 mr-1" />Porcentaje</SelectItem>
                  <SelectItem value="fixed">Monto fijo (USD)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Valor del descuento *</label>
              <Input type="number" min="0" step="0.01" value={form.discount_value}
                onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
                placeholder={form.discount_type === "percentage" ? "10 (= 10%)" : "25"}
                className="mt-1" data-testid="coupon-value" />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-600">Aplica a</label>
              <div className="mt-1 flex gap-2 flex-wrap">
                {[["all", "Todos los paquetes"], ["products", "Paquetes específicos"], ["sedes", "Sede(s)"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setForm((f) => ({ ...f, scope: v }))}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                      form.scope === v ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`scope-${v}`}>{l}</button>
                ))}
              </div>
            </div>

            {form.scope === "products" && (
              <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3 max-h-40 overflow-y-auto">
                {refs.products.length === 0 ? <p className="text-xs text-slate-500">No hay paquetes creados aún.</p> :
                  refs.products.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                      <input type="checkbox"
                        checked={form.product_ids.includes(p.id)}
                        onChange={() => setForm((f) => ({ ...f, product_ids: toggleInList(f.product_ids, p.id) }))}
                        data-testid={`scope-prod-${p.id}`} />
                      <span>{p.name} · <span className="text-slate-500">${Number(p.price || 0).toFixed(0)}</span></span>
                    </label>
                  ))
                }
              </div>
            )}

            {form.scope === "sedes" && (
              <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
                {refs.sedes.length === 0 ? <p className="text-xs text-slate-500">Sin sedes.</p> :
                  refs.sedes.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                      <input type="checkbox"
                        checked={form.sede_ids.includes(s.id)}
                        onChange={() => setForm((f) => ({ ...f, sede_ids: toggleInList(f.sede_ids, s.id) }))}
                        data-testid={`scope-sede-${s.id}`} />
                      <span>{s.name}</span>
                    </label>
                  ))
                }
              </div>
            )}

            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-600">Afiliado (opcional) — atribuye la venta y genera comisión</label>
              <Select value={form.affiliate_code || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, affiliate_code: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="mt-1" data-testid="coupon-affiliate"><SelectValue placeholder="Sin afiliado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin afiliado —</SelectItem>
                  {refs.affiliates.map((a) => <SelectItem key={a.id} value={a.code}>{a.code} · {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Vigente desde (opcional)</label>
              <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))} className="mt-1" data-testid="coupon-starts" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Vigente hasta (opcional)</label>
              <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))} className="mt-1" data-testid="coupon-ends" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Máximo de usos (opcional)</label>
              <Input type="number" min="1" value={form.max_uses} onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))} placeholder="Sin límite" className="mt-1" data-testid="coupon-max-uses" />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.one_per_email}
                  onChange={(e) => setForm((f) => ({ ...f, one_per_email: e.target.checked }))}
                  data-testid="coupon-one-per-email" />
                <span>Un uso por correo</span>
              </label>
            </div>

            <div className="sm:col-span-2 flex flex-wrap gap-4 pt-1">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  data-testid="coupon-active" />
                <span>Activo</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Cancelar</Button>
            <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="save-coupon">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar cupón</AlertDialogTitle>
            <AlertDialogDescription>Se quitará "{toDelete?.code}". Los pedidos que ya lo usaron se mantienen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* --------------------------- PAGE --------------------------- */
export default function StoreOrders() {
  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6" data-testid="store-orders-page">
      <header className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Store className="h-6 w-6" /> Pedidos de la tienda
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Revisá los pedidos que llegan de <code className="rounded bg-slate-100 px-1">/paquetes</code>, aprobalos y publicá nuevos paquetes.
        </p>
      </header>
      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders" data-testid="tab-orders"><Package className="h-4 w-4 mr-1" /> Pedidos</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products"><Store className="h-4 w-4 mr-1" /> Paquetes</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="tab-coupons"><Tag className="h-4 w-4 mr-1" /> Cupones</TabsTrigger>
          <TabsTrigger value="embed" data-testid="tab-embed"><Code2 className="h-4 w-4 mr-1" /> Embeber</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-4"><OrdersPanel /></TabsContent>
        <TabsContent value="products" className="mt-4"><ProductsPanel /></TabsContent>
        <TabsContent value="coupons" className="mt-4"><CouponsPanel /></TabsContent>
        <TabsContent value="embed" className="mt-4"><EmbedPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
